import { LaneDirection, LaneInput, ManeuverInput } from '@kinetic-ui/data-layer';
import { Lane as LaneComponent } from 'components/Lane';
import { Position } from 'deck.gl';
import { nanoid } from 'nanoid';
import { useContext, useEffect, useMemo } from 'react';
import { MANEUVERS_FORM_ID } from '../../../const';
import { LocationFormContext } from '../../../context';
import { useSelectedManeuver } from '../hooks/useSelectedManeuver';
import { useSelectedManeuverLane } from '../hooks/useSelectedManeuverLane';
import { getUnconnectedIds } from './utils';
import { RecursivePartial } from 'types';
import { MapSVGOverlay } from 'components/locations';
import ManeuverPath from 'components/ManeuverPath';

export const EditMapManeuvers: React.FunctionComponent = () => {
  const { values, setValues, setDirty } = useContext(LocationFormContext);

  const { selectedManeuver, setSelectedManeuver } = useSelectedManeuver();
  const { selectedManeuverLane, setSelectedManeuverLane } = useSelectedManeuverLane();

  useEffect(() => {
    if (selectedManeuver) {
      setDirty(MANEUVERS_FORM_ID, true);
      setValues((prev) => {
        const index = prev?.maneuvers?.findIndex((found) => found.id === selectedManeuver.id) ?? 0;
        const newManeuvers =
          prev?.maneuvers?.filter((found) => found.id !== selectedManeuver.id) ?? [];
        newManeuvers.splice(index, 0, selectedManeuver);
        return {
          ...prev,
          maneuvers: newManeuvers,
        };
      });
    }
  }, [selectedManeuver, setValues, setDirty]);

  const lanes = useMemo(() => values?.lanes ?? [], [values?.lanes]);
  const maneuvers = useMemo(() => values?.maneuvers ?? [], [values?.maneuvers]);

  const unconnectedIds = useMemo(
    () => getUnconnectedIds(selectedManeuverLane, maneuvers, lanes),
    [lanes, maneuvers, selectedManeuverLane]
  );

  const isConnectableLane = (lane: RecursivePartial<LaneInput>) =>
    selectedManeuverLane &&
    unconnectedIds.includes(lane.id) &&
    (!selectedManeuver ||
      (selectedManeuver &&
        selectedManeuver.refApproachLane !== lane.id &&
        selectedManeuver.refDepartureLane !== lane.id));

  const onClickLane = (lane: RecursivePartial<LaneInput>) => {
    setSelectedManeuverLane(lane);
    setSelectedManeuver(undefined);
  };

  const onClickConnect = (lane: RecursivePartial<LaneInput>) => {
    const isApproach = lane.laneDirection === LaneDirection.Approach;

    const newManeuver: Partial<ManeuverInput> = { id: nanoid() };

    if (isApproach) {
      newManeuver.refApproachLane = lane.id;
      newManeuver.refDepartureLane = selectedManeuverLane?.id;
    } else {
      newManeuver.refDepartureLane = lane.id;
      newManeuver.refApproachLane = selectedManeuverLane?.id;
    }

    setSelectedManeuver(newManeuver as ManeuverInput);
  };

  const onSelectManeuver = (maneuver: RecursivePartial<ManeuverInput>) => {
    setSelectedManeuverLane(
      lanes.find(
        (lane) =>
          lane.laneDirection === LaneDirection.Approach && maneuver.refApproachLane === lane.id
      )
    );
    setSelectedManeuver(maneuver as ManeuverInput);
  };

  const renderableManeuvers = maneuvers.filter((maneuver) => {
    // selectedManeuver is rendered in a separate graphic
    if (!selectedManeuverLane) {
      return false;
    }

    return (
      (selectedManeuverLane.laneDirection === LaneDirection.Approach &&
        maneuver.refApproachLane === selectedManeuverLane.id) ||
      (selectedManeuverLane.laneDirection === LaneDirection.Departure &&
        maneuver.refDepartureLane === selectedManeuverLane.id)
    );
  });

  return (
    <MapSVGOverlay>
      <g>
        {lanes.map((lane) => (
          <LaneComponent
            selected={lane.id === selectedManeuverLane?.id}
            coordinates={values?.geometry?.coordinates as Position}
            key={lane.id}
            connectable={isConnectableLane(lane)}
            departure={lane.laneDirection === LaneDirection.Departure}
            lane={lane}
            onClickLane={() => onClickLane(lane)}
            onClickConnect={() => onClickConnect(lane)}
          />
        ))}
      </g>

      {renderableManeuvers.map((maneuver) => (
        <ManeuverPath
          key={maneuver.id}
          location={values}
          maneuver={maneuver}
          onClick={onSelectManeuver}
        />
      ))}

      {selectedManeuver ? (
        <ManeuverPath selected location={values} maneuver={selectedManeuver} />
      ) : null}
    </MapSVGOverlay>
  );
};
